import React, { type ReactElement, useCallback, useMemo, useState } from 'react';

import classnames from 'classnames';

import { type SizeType, useSize } from '../../features/config';
import { alphanumericalSort, SORTING_MODES } from '../../utils';

import { ExpandButton } from './ExpandButton';
import { useExpand } from './hooks/useExpand';
import { TableExtraColumn } from './Table.constants';
import { getColKey, getRowKey } from './Table.helpers';
import {
    type ColumnConfig,
    type ExpandableConfig,
    type Key,
    type RowKey,
    type TableColumnType,
    type TableResourceRecord,
    type TableSelectionConfig
} from './Table.types';
import { TableBody } from './TableBody/TableBody';
import { TableHeader } from './TableHeader/TableHeader';
import TableSelectionAllControl from './TableSelectionAllControl';
import TableSelectionControl from './TableSelectionControl';

import styles from './Table.module.scss';

export type TableProps<RecordType = unknown> = {
    /**
     * It's an array that can contain `ColumnConfig` and `TableExtraColumn`. <br />
     * - `ColumnConfig` has the following main properties: <br />
     *   - `key` is required for column sorting and should be unique across columns. <br />
     *   - `dataIndex` points to the corresponding key in the data record. <br />
     *   - `title` is the column title. <br />
     *   - `render` is a function that can be used to customize the rendering of the cell. <br />
     *   - `width` is the width of the column. <br />
     *   - `main` is a boolean that can be used to mark the column as the main one applying a bolder text style to it. <br />
     * - `TableExtraColumn` is an enum that can be used to reorder special columns. <br />
     *   - `TableExtraColumn.EXPAND`: changes the position of the "expand button" column. <br />
     */
    columns: TableColumnType<RecordType>[];
    /**
     * Data record array to be displayed
     */
    dataSource: RecordType[];
    /**
     * Custom row key getter, defaults to `row.key`
     */
    rowKey?: RowKey<RecordType>;
    expandable?: ExpandableConfig<RecordType>;
    /**
     * Use size to change the spacing of the rows. `default` stands between `small` and `large`.
     */
    size?: SizeType;
    /**
     * Set sticky header and scroll bar
     */
    sticky?: boolean;
    height?: number | string;
    maxHeight?: number | string;
    className?: string;
    selection?: TableSelectionConfig<RecordType>;
    /**
     * Disable client side sorting functionality (Due to some cases required server side sorting)
     */
    disableClientSorting?: boolean;
    /**
     *  Callback function when sort button is clicked
     */
    onSortClick?: (key: string, mode: SORTING_MODES) => void;
};

export const Table = <RecordType extends TableResourceRecord = any>({
    columns,
    dataSource,
    className,
    rowKey = 'key',
    expandable,
    size: sizeProp,
    sticky,
    height,
    maxHeight,
    selection,
    disableClientSorting,
    onSortClick,
    ...rest
}: TableProps<RecordType>): ReactElement => {
    const withInnerScroll = Boolean(sticky || height || maxHeight);
    const createRowKey = useMemo(() => getRowKey(rowKey), [rowKey]);
    const [headerColumnsWidths, setHeaderColumnsWidths] = useState<Map<Key, number>>(new Map());
    const [sortKey, setSortKey] = useState<string | null>(null);
    const [sortMode, setSortMode] = useState<SORTING_MODES>(SORTING_MODES.NORMAL);
    const tableHeaderRef = React.useRef<HTMLTableSectionElement>(null);
    const tableBodyRef = React.useRef<HTMLDivElement>(null);
    const size = useSize(sizeProp, 'medium', 'Table');

    const handleSortClick = (newSortKey: string) => {
        const isSameKey = sortKey === newSortKey || sortKey === null;
        const newSortMode =
            isSameKey && sortMode === SORTING_MODES.NORMAL
                ? SORTING_MODES.REVERSE
                : SORTING_MODES.NORMAL;

        setSortKey(newSortKey);
        setSortMode(newSortMode);
        onSortClick?.(newSortKey, newSortMode);
    };

    const {
        expandedRowRender,
        rowExpandable,
        isTableExpandable,
        mergedExpandedKeys,
        onTriggerExpand,
        expandColumn
    } = useExpand({
        expandable,
        createRowKey,
        // eslint-disable-next-line react/no-unstable-nested-components
        button: (record, isExpanded) => (
            <ExpandButton
                record={record}
                isExpanded={isExpanded}
                onTriggerExpand={onTriggerExpand}
            />
        )
    });

    const mergedColumns = useMemo<ColumnConfig<RecordType>[]>(() => {
        const result = columns.slice() as ColumnConfig<RecordType>[];
        // Add selection column
        if (selection) {
            const selectionColumn = {
                key: TableExtraColumn.Selection,
                width: 0,
                dataIndex: TableExtraColumn.Selection,
                title: '',
                // eslint-disable-next-line react/display-name
                renderTitle: () => (
                    <TableSelectionAllControl selection={selection} dataSource={dataSource} />
                ),
                // eslint-disable-next-line react/display-name
                render: (_value, record) => (
                    <TableSelectionControl
                        selection={selection}
                        record={record}
                        dataSource={dataSource}
                    />
                )
            } as ColumnConfig<RecordType>;
            result.unshift(selectionColumn);
        }

        // Add expand column
        if (isTableExpandable) {
            const expandColumnIndex = columns.findIndex(
                (column) => getColKey(column) === TableExtraColumn.Expand
            );

            if (expandColumnIndex === -1) {
                // If the expand column doesn't exist, insert one at the beginning
                result.unshift(expandColumn);
            } else {
                // If the expand column exists, replace the enum with the column config
                result[expandColumnIndex] = expandColumn;
            }
        }

        return result;
    }, [columns, isTableExpandable, expandColumn]);
    const columnsKey = mergedColumns.map((col) => getColKey(col));

    const headerColumns = useMemo(() => {
        const result = [...mergedColumns];
        if (withInnerScroll) {
            result.push({
                key: TableExtraColumn.Scrollbar,
                width: 0,
                dataIndex: TableExtraColumn.Scrollbar,
                title: ''
            } as ColumnConfig<RecordType>);
        }
        return result;
    }, [mergedColumns]);

    const onColumnsResize = useCallback((value: Map<Key, number>) => {
        const newHeaderColumnsWidths = new Map(value);
        const tableBodyOffsetWidth = tableBodyRef.current?.offsetWidth ?? 0;
        const tableBodyClientWidth = tableBodyRef.current?.clientWidth ?? 0;
        const scrollbarSize =
            tableBodyOffsetWidth > tableBodyClientWidth
                ? tableBodyOffsetWidth - tableBodyClientWidth
                : 0;
        newHeaderColumnsWidths.set(TableExtraColumn.Scrollbar, scrollbarSize);
        setHeaderColumnsWidths(newHeaderColumnsWidths);
    }, []);

    const bodyColsWidths = useMemo(() => {
        return new Map(mergedColumns.map((col) => [getColKey(col), col.width]));
    }, [mergedColumns]);

    const Wrapper = withInnerScroll ? 'div' : 'table';

    const sorterFunc = useMemo(
        () =>
            // Custom client side sorting
            mergedColumns.find((column) => column.key === sortKey)?.sorter ||
            // Default client side sorting
            alphanumericalSort(sortKey, sortMode),
        [mergedColumns, sortKey, sortMode]
    );
    const sortedDataSource = useMemo(
        () => (sortKey && !disableClientSorting && dataSource?.sort(sorterFunc)) || dataSource,
        [dataSource, disableClientSorting, sortKey, sorterFunc]
    );

    return (
        <div
            className={classnames(styles.tableWrapper, className, size && styles[size], {
                [styles.sticky]: sticky
            })}
            {...rest}
        >
            <Wrapper>
                <TableHeader
                    ref={tableHeaderRef}
                    columns={headerColumns}
                    colsWidths={headerColumnsWidths}
                    withInnerScroll={withInnerScroll}
                    sortKey={sortKey}
                    sortMode={sortMode}
                    onSortBy={handleSortClick}
                />
                <TableBody
                    ref={tableBodyRef}
                    dataSource={sortedDataSource}
                    columns={mergedColumns}
                    isTableExpandable={isTableExpandable}
                    createRowKey={createRowKey}
                    expandedRowRender={expandedRowRender}
                    rowExpandable={rowExpandable}
                    maxHeight={maxHeight}
                    height={height}
                    expandedKeys={mergedExpandedKeys}
                    withInnerScroll={withInnerScroll}
                    colsWidths={bodyColsWidths}
                    onColumnsResize={onColumnsResize}
                    columnsKey={columnsKey}
                />
            </Wrapper>
        </div>
    );
};
